import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import DashboardHeader from './components/DashboardHeader';
import ShipmentFilters from './components/ShipmentFilters';
import ShipmentTable from './components/ShipmentTable';
import ShipmentTableMobile from './components/ShipmentTableMobile';
import { adminService } from '../../services/adminService';
import { supabase } from '../../lib/supabase';
import { realtimeService } from '../../services/realtimeService';

import { trackSearch, trackButtonClick, trackNavigation } from '../../services/analyticsService';

// Terminal statuses — not counted as "En cours"
const TERMINAL_STATUSES = ['livre', 'annule', 'delivered', 'exception'];

export default function AdminShipmentsDashboard() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [archiveView, setArchiveView] = useState('actifs'); // 'actifs' | 'archives' | 'tous'
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    type: 'all',
    city: '',
    startDate: '',
    endDate: ''
  });

  const [isMobile, setIsMobile] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [userRole, setUserRole] = useState('admin');

  // Detect admin role from localStorage
  useEffect(() => {
    try {
      const auth = JSON.parse(localStorage.getItem('adtAdminAuth') || '{}');
      if (auth?.role) setUserRole(auth?.role);
    } catch (_) {}
  }, []);

  // Subscribe to real-time shipments updates
  useEffect(() => {
    if (!isAuthenticated || userRole !== 'admin') return;

    const subscription = realtimeService?.subscribeToShipments((payload) => {
      if (payload?.eventType === 'INSERT') {
        setShipments(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setShipments(prev => prev?.map(shipment =>
          shipment?.id === payload?.new?.id ? { ...shipment, ...payload?.new } : shipment
        ));
      } else if (payload?.eventType === 'DELETE') {
        setShipments(prev => prev?.filter(shipment => shipment?.id !== payload?.old?.id));
      }
    });

    return () => {
      realtimeService?.unsubscribe('shipments-realtime');
    };
  }, [isAuthenticated, userRole]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadShipments();
  }, []);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getAllShipmentsWithTotals();
      setShipments(data);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading shipments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('adtAdminAuth');
      await supabase?.auth?.signOut();
      navigate('/admin-login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('adtAdminAuth');
      navigate('/admin-login', { replace: true });
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'search' && value) {
      trackSearch('shipment_search', value, shipments?.length);
    } else if (key === 'status' && value !== 'all') {
      trackSearch('shipment_filter_status', value);
    } else if (key === 'type' && value !== 'all') {
      trackSearch('shipment_filter_type', value);
    }
  };

  const handleResetFilters = () => {
    setFilters({ search: '', status: 'all', type: 'all', city: '', startDate: '', endDate: '' });
  };

  // Archive view filter
  const archiveFilteredShipments = shipments?.filter(shipment => {
    if (archiveView === 'actifs') return !shipment?.isArchived;
    if (archiveView === 'archives') return shipment?.isArchived;
    return true; // 'tous'
  });

  const filteredShipments = archiveFilteredShipments?.filter(shipment => {
    const matchesSearch = filters?.search === '' ||
      shipment?.reference?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      shipment?.senderName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      shipment?.receiverName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      String(shipment?.senderPhone || '')?.toLowerCase()?.includes(filters?.search?.toLowerCase());

    const matchesStatus = filters?.status === 'all' || shipment?.status === filters?.status;
    const matchesType = filters?.type === 'all' || shipment?.type === filters?.type;
    const matchesCity = filters?.city === '' ||
      shipment?.senderCity?.toLowerCase()?.includes(filters?.city?.toLowerCase()) ||
      shipment?.receiverCity?.toLowerCase()?.includes(filters?.city?.toLowerCase());

    const matchesDateRange = (!filters?.startDate || new Date(shipment.createdAt) >= new Date(filters.startDate)) &&
                             (!filters?.endDate || new Date(shipment.createdAt) <= new Date(filters.endDate));

    return matchesSearch && matchesStatus && matchesType && matchesCity && matchesDateRange;
  });

  const handleExportCSV = () => {
    trackButtonClick('export_shipments_csv', { count: filteredShipments?.length });
    adminService?.exportShipmentsToCSV(filteredShipments);
  };

  const handleStatusUpdate = (shipmentId) => {
    trackNavigation(`/admin/shipments/${shipmentId}`, '/admin-shipments-dashboard');
    navigate(`/admin/shipments/${shipmentId}`);
  };

  const handleShipmentDeleted = (deletedId) => {
    setShipments(prev => prev?.filter(s => s?.id !== deletedId));
  };

  const handleShipmentArchived = (shipmentId, archived) => {
    setShipments(prev => prev?.map(s =>
      s?.id === shipmentId
        ? { ...s, isArchived: archived, archivedAt: archived ? new Date()?.toISOString() : null }
        : s
    ));
  };

  // KPI: Total non-archived shipments
  const totalShipments = shipments?.filter(s => !s?.isArchived)?.length;

  // KPI: En cours = non-archived AND status not terminal
  const activeShipments = shipments?.filter(s =>
    !s?.isArchived && !TERMINAL_STATUSES?.includes(s?.status)
  )?.length;

  const archiveTabClass = (tab) =>
    `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      archiveView === tab
        ? 'bg-green-600 text-white shadow-sm'
        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
    }`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation onLogout={handleLogout} />
        <BreadcrumbTrail customItems={[]} />
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
          <DashboardHeader totalShipments={totalShipments} activeShipments={activeShipments} />
          <div className="text-center py-8 sm:py-12">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-sm sm:text-base text-gray-600 mt-4">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation onLogout={handleLogout} />
        <BreadcrumbTrail customItems={[]} />
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
          <DashboardHeader totalShipments={totalShipments} activeShipments={activeShipments} />
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-sm sm:text-base text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation onLogout={handleLogout} />
      <BreadcrumbTrail customItems={[]} />
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <DashboardHeader totalShipments={totalShipments} activeShipments={activeShipments} />

        {/* Archive view tabs */}
        <div className="flex items-center gap-2 mb-4">
          <button className={archiveTabClass('actifs')} onClick={() => setArchiveView('actifs')}>
            Actifs
          </button>
          <button className={archiveTabClass('archives')} onClick={() => setArchiveView('archives')}>
            Archivés
          </button>
          <button className={archiveTabClass('tous')} onClick={() => setArchiveView('tous')}>
            Tous
          </button>
        </div>

        <ShipmentFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleResetFilters}
          onExportCSV={handleExportCSV}
          resultsCount={filteredShipments?.length || 0}
        />

        {!loading && !error && (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block">
              <ShipmentTable
                shipments={filteredShipments}
                onStatusUpdate={handleStatusUpdate}
                onDeleted={handleShipmentDeleted}
                onArchived={handleShipmentArchived}
                userRole={userRole}
              />
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden">
              <ShipmentTableMobile
                shipments={filteredShipments}
                onStatusUpdate={handleStatusUpdate}
                onArchived={handleShipmentArchived}
                onDeleted={handleShipmentDeleted}
                userRole={userRole}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}